import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

/**
 * A component that conditionally renders its children based on user permissions.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.auth - The authentication object containing user details.
 * @param {string} props.permission - The required permission to render the children.
 * @param {React.ReactNode} props.children - The children components to render if the user has the required permission.
 * @param {boolean} props.overridePermission - If true, renders children without checking permissions.
 * @returns {React.ReactElement|null} - The rendered children if the user has the required permission, otherwise null.
 */
const AuthResource = ({ auth, permission, children, overridePermission }) => {
    if (
        overridePermission ||
        (auth &&
            auth.resourceDetails &&
            auth.resourceDetails.permissionMap &&
            auth.resourceDetails.permissionMap[permission])
    ) {
        return <>{children}</>;
    }

    return <React.Fragment></React.Fragment>;
};

AuthResource.propTypes = {
    auth: PropTypes.object,
    permission: PropTypes.string,
    children: PropTypes.node,
    overridePermission: PropTypes.bool,
};

/**
 * Maps the Redux state to the component props.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} state.auth - The authentication state.
 * @returns {Object} The mapped props.
 */

const mapStateToProps = ({ auth }) => ({
    auth,
});

/**
 * Maps the dispatch function to the component props.
 *
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {Object} The mapped props with action creators.
 */
const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({}, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthResource);
